/**
 * What a functional block is: its footprint, the ports where it connects, and the
 * eight orientations rotation and mirroring give it for free.
 *
 * Plain JSON types and pure functions only -- no engine imports, nothing stateful.
 * The admin editor authors these, the placement rules in `./placement.ts` apply them,
 * and a level stores a reference to a block id rather than the block itself.
 *
 * See develop/DungeonBlocks.md §4.1-4.4. Two conventions are fixed here and
 * everything downstream depends on them:
 *
 * - **North is -z and south is +z**, east +x and west -x, so a plan view with north up
 *   reads the way `DungeonTile.z` ("grid row") already does: rows grow downward.
 * - **Mirror first, then rotate.** An orientation is one mirror in x applied in the
 *   block's own frame, then a clockwise rotation. Composing them the other way names
 *   the same eight orientations by different numbers, so the order has to be stated.
 */
import type { TileRotation } from '../level.ts';

/** What a block contributes to a run, and the category the palette groups it under. */
export const BLOCK_ROLES = ['link', 'room', 'goal', 'threat'] as const;
export type BlockRole = (typeof BLOCK_ROLES)[number];

/** Which side of a cell a port opens through. */
export const SIDES = ['north', 'east', 'south', 'west'] as const;
export type Side = (typeof SIDES)[number];

/** A block definition id, as the catalogue keys it: `room.storeroom`. */
export type BlockId = string;

/** Rotations are the tile grid's: blocks line up with the modular art. */
export type BlockRotation = TileRotation;

/** Grid cell. Same frame as `DungeonTile`: x is the column, z the row. */
export interface Cell {
  x: number;
  z: number;
}

/**
 * The footprint sizes a block may be authored at, from §4.3's table -- the document
 * recommends "five sizes" and then lists seven, so the list is what is encoded and
 * nothing is invented. `kind` is the name the palette shows.
 *
 * A footprint is matched as authored, not as rotated: an author picks 1x2 and rotates
 * the block, rather than there being a 2x1 to pick. That is what keeps the palette
 * short while rotation still supplies the other orientations.
 */
export const FOOTPRINT_SIZES = [
  { kind: 'link', width: 1, depth: 1 },
  { kind: 'passage', width: 1, depth: 2 },
  { kind: 'passage', width: 1, depth: 3 },
  { kind: 'small', width: 2, depth: 2 },
  { kind: 'medium', width: 3, depth: 3 },
  { kind: 'large', width: 4, depth: 4 },
  { kind: 'hall', width: 3, depth: 5 },
] as const;

export type FootprintKind = (typeof FOOTPRINT_SIZES)[number]['kind'];

/** Extents in cells. West-east is `width`, north-south is `depth`. */
export interface Footprint {
  width: number;
  depth: number;
}

/** True when the pair is one of the authored sizes above. */
export function isAllowedFootprint(footprint: Footprint): boolean {
  return FOOTPRINT_SIZES.some(
    (size) => size.width === footprint.width && size.depth === footprint.depth,
  );
}

/**
 * The opening a block connects through: a boundary cell, which side of it opens, and
 * how many cell edges wide the opening is.
 *
 * One port size is the whole contract (§4.2: 2.5 m x 3 m, centred on the cell edge).
 * `width: 2` is the double port -- two adjacent cell edges -- and `cell` is then the
 * first of the pair along the side's run direction: +x for north and south, +z for
 * east and west.
 */
export type PortWidth = 1 | 2;

export interface Port {
  cell: Cell;
  side: Side;
  width: PortWidth;
}

/** A block as authored: content the editor writes and a level refers to. */
export interface BlockDefinition {
  id: BlockId;
  /** What the palette shows: "Storeroom". */
  label: string;
  role: BlockRole;
  footprint: Footprint;
  ports: Port[];
}

/** One block on a board: which definition, where its footprint origin sits, and how it is turned. */
export interface PlacedBlock {
  /** Instance id, so a lock or a key can name this one. */
  instanceId: string;
  block: BlockId;
  /** Footprint origin, in cells -- its minimum-x, minimum-z corner once oriented. */
  at: Cell;
  rotation: BlockRotation;
  mirrored?: boolean;
}

/** The side directly across a cell edge from this one. */
export function oppositeSide(side: Side): Side {
  switch (side) {
    case 'north':
      return 'south';
    case 'south':
      return 'north';
    case 'east':
      return 'west';
    case 'west':
      return 'east';
  }
}

/** Unit step from a cell to its neighbour across `side`. */
export function sideStep(side: Side): Cell {
  switch (side) {
    case 'north':
      return { x: 0, z: -1 };
    case 'south':
      return { x: 0, z: 1 };
    case 'east':
      return { x: 1, z: 0 };
    case 'west':
      return { x: -1, z: 0 };
  }
}

/**
 * Which way a double port runs along its own edge: +x for a north or south opening,
 * +z for an east or west one. Rotating a block rotates this with it, which is why a
 * width-2 port is rebuilt from its cells rather than carried across unchanged.
 */
export function sideRun(side: Side): Cell {
  return side === 'north' || side === 'south' ? { x: 1, z: 0 } : { x: 0, z: 1 };
}

/** The cells a port spans: one, or the adjacent pair a double port opens through. */
export function portCells(port: Port): Cell[] {
  const run = sideRun(port.side);
  const cells: Cell[] = [];
  for (let i = 0; i < port.width; i += 1) {
    cells.push({ x: port.cell.x + run.x * i, z: port.cell.z + run.z * i });
  }
  return cells;
}

/** Quarter turns clockwise in plan view, north up. */
function quarterTurns(rotation: BlockRotation): number {
  return (rotation / 90) % 4;
}

/** Mirror in x, in the block's own frame. Left-right flip: the z row is untouched. */
export function mirrorCell(cell: Cell): Cell {
  return { x: -cell.x, z: cell.z };
}

/** Clockwise with north up and z growing downward: (x, z) -> (-z, x). */
export function rotateCell(cell: Cell, rotation: BlockRotation): Cell {
  let turned = cell;
  for (let i = 0; i < quarterTurns(rotation); i += 1) {
    turned = { x: -turned.z, z: turned.x };
  }
  return turned;
}

/** Mirroring in x swaps west and east and leaves north and south alone. */
export function mirrorSide(side: Side): Side {
  return side === 'east' || side === 'west' ? oppositeSide(side) : side;
}

export function rotateSide(side: Side, rotation: BlockRotation): Side {
  const index = SIDES.indexOf(side);
  const turned = SIDES[(index + quarterTurns(rotation)) % SIDES.length];
  // SIDES is non-empty and the index is taken modulo its length, so this holds.
  return turned ?? side;
}

/** Mirror then rotate, as the header states. */
export function orientCell(cell: Cell, rotation: BlockRotation, mirrored = false): Cell {
  return rotateCell(mirrored ? mirrorCell(cell) : cell, rotation);
}

export function orientSide(side: Side, rotation: BlockRotation, mirrored = false): Side {
  return rotateSide(mirrored ? mirrorSide(side) : side, rotation);
}

export function translateCell(cell: Cell, by: Cell): Cell {
  return { x: cell.x + by.x, z: cell.z + by.z };
}

/**
 * The same opening, turned. The cells move and the side turns with them, and the
 * anchor is re-chosen as the first cell along the *new* run direction -- a double
 * port rotated 90 degrees anchors on its other cell, which is the half usually missed.
 *
 * The result is in the rotated frame, which has negative coordinates until a caller
 * translates it; `orientBlock` in `./placement.ts` is what normalises the pair together.
 */
export function orientPort(port: Port, rotation: BlockRotation, mirrored = false): Port {
  const side = orientSide(port.side, rotation, mirrored);
  const cells = portCells(port).map((cell) => orientCell(cell, rotation, mirrored));
  const run = sideRun(side);
  const anchor = cells.reduce((first, cell) =>
    cell.x * run.x + cell.z * run.z < first.x * run.x + first.z * run.z ? cell : first,
  );
  return { cell: anchor, side, width: port.width };
}

export function translatePort(port: Port, by: Cell): Port {
  return { cell: translateCell(port.cell, by), side: port.side, width: port.width };
}

/** True when the port's cells are all inside the footprint and open outward through its boundary. */
export function isPortOnBoundary(footprint: Footprint, port: Port): boolean {
  const step = sideStep(port.side);
  return portCells(port).every((cell) => {
    const inside = cell.x >= 0 && cell.x < footprint.width && cell.z >= 0 && cell.z < footprint.depth;
    const beyond = translateCell(cell, step);
    const leaves =
      beyond.x < 0 || beyond.x >= footprint.width || beyond.z < 0 || beyond.z >= footprint.depth;
    return inside && leaves;
  });
}

/**
 * Every single-width opening a footprint could carry, in reading order per side. This is
 * the set of edges the editor offers for a click; a double port is two of these.
 */
export function boundaryPorts(footprint: Footprint): Port[] {
  const ports: Port[] = [];
  for (const side of SIDES) {
    for (let z = 0; z < footprint.depth; z += 1) {
      for (let x = 0; x < footprint.width; x += 1) {
        const port: Port = { cell: { x, z }, side, width: 1 };
        if (isPortOnBoundary(footprint, port)) ports.push(port);
      }
    }
  }
  return ports;
}

export function sameCell(a: Cell, b: Cell): boolean {
  return a.x === b.x && a.z === b.z;
}
